/**
 * O que faz: Carrega mensagens recentes primeiro e recupera mensagens antigas por cursor.
 * Onde usa: ChatWindow da rota /chat.
 * Conexões: RPCs de mensagens, Supabase Realtime e TanStack Query.
 * Tabelas: chat_mensagens e relações de follow-up por meio das RPCs.
 * Data: 23/07/2026.
 */
import { useEffect, useMemo } from 'react';
import { type InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  appendRealtimeMessage,
  flattenMessagesForDisplay,
  mergeRecoveredMessages,
} from '@/features/chat/messages/chatMessageCache';
import { chatMessageKeys } from '@/features/chat/messages/chatMessageQueryKeys';
import type {
  ChatMessage,
  ChatMessageCursor,
  ChatMessagePage,
} from '@/features/chat/messages/chatMessageTypes';
import { supabase } from '@/integrations/supabase/client';

type MessageRpcRow = Record<string, unknown>;

export const normalizarMensagem = (row: MessageRpcRow): ChatMessage => ({
  id: String(row.id),
  clinica_id: String(row.clinica_id),
  lead_id: String(row.lead_id),
  conteudo: typeof row.conteudo === 'string' ? row.conteudo : null,
  enviado_por: typeof row.enviado_por === 'string' ? row.enviado_por : 'usuario',
  tipo: typeof row.tipo === 'string' ? row.tipo : null,
  anexo_url: typeof row.anexo_url === 'string' ? row.anexo_url : null,
  nome_arquivo: typeof row.nome_arquivo === 'string' ? row.nome_arquivo : null,
  lida: Boolean(row.lida),
  created_at: String(row.created_at),
  is_followup_trigger: Boolean(row.is_followup_trigger),
  followup_trigger_campaign_id: typeof row.followup_trigger_campaign_id === 'string'
    ? row.followup_trigger_campaign_id : null,
  follow_up_execucao_id: typeof row.follow_up_execucao_id === 'string'
    ? row.follow_up_execucao_id : null,
  follow_up_execucoes: (row.follow_up_execucoes as ChatMessage['follow_up_execucoes']) ?? null,
  // A RPC antiga pode não retornar o identificador do WhatsApp; null preserva o contrato do cache.
  id_message_whats: typeof row.id_message_whats === 'string' ? row.id_message_whats : null,
});

const fetchMessagePage = async ({
  clinicaId,
  leadId,
  cursor,
  signal,
}: {
  clinicaId: string;
  leadId: string;
  cursor: ChatMessageCursor | null;
  signal: AbortSignal;
}): Promise<ChatMessagePage> => {
  // RPC get_chat_messages_page: SELECT do histórico de uma conversa.
  // Filtros: clínica, lead e cursor de mensagens mais antigas.
  // Risco: conteúdo de conversa é sensível e não deve ser registrado nem persistido.
  const { data, error } = await supabase
    .rpc('get_chat_messages_page' as never, {
      p_clinica_id: clinicaId,
      p_lead_id: leadId,
      p_before_at: cursor?.createdAt ?? null,
      p_before_id: cursor?.id ?? null,
      p_limit: 50,
    } as never)
    .abortSignal(signal);

  if (error) throw new Error(`Não foi possível carregar as mensagens: ${error.message}`);

  const rows = ((data ?? []) as MessageRpcRow[]).map(normalizarMensagem);
  const visibleRows = rows.slice(0, 50);
  const oldest = visibleRows[visibleRows.length - 1];

  return {
    messages: [...visibleRows].reverse(),
    olderCursor: rows.length > 50 && oldest
      ? { createdAt: oldest.created_at, id: oldest.id }
      : null,
  };
};

const fetchMessagesAfter = async (
  clinicaId: string,
  leadId: string,
  cursor: ChatMessageCursor,
  signal: AbortSignal,
) => {
  // RPC get_chat_messages_after: SELECT das mensagens que podem ter chegado em uma lacuna Realtime.
  // Filtros: clínica, lead e última mensagem conhecida.
  // Risco: conteúdo de conversa é sensível e permanece somente no cache da sessão.
  const { data, error } = await supabase.rpc('get_chat_messages_after' as never, {
    p_clinica_id: clinicaId,
    p_lead_id: leadId,
    p_after_at: cursor.createdAt,
    p_after_id: cursor.id,
    p_limit: 100,
  } as never).abortSignal(signal);

  if (error) throw new Error(`Não foi possível reconciliar as mensagens: ${error.message}`);
  return ((data ?? []) as MessageRpcRow[]).map(normalizarMensagem);
};

export const useConversationMessagesInfinite = ({
  clinicaId,
  leadId,
}: {
  clinicaId: string | null | undefined;
  leadId: string | null | undefined;
}) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => clinicaId && leadId ? chatMessageKeys.conversation(clinicaId, leadId) : null,
    [clinicaId, leadId],
  );

  const query = useInfiniteQuery({
    queryKey: queryKey ?? [...chatMessageKeys.all, 'disabled'],
    queryFn: ({ pageParam, signal }) => fetchMessagePage({
      clinicaId: clinicaId!,
      leadId: leadId!,
      cursor: pageParam as ChatMessageCursor | null,
      signal,
    }),
    initialPageParam: null as ChatMessageCursor | null,
    getNextPageParam: page => page.olderCursor,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    maxPages: 20,
    refetchOnWindowFocus: false,
    enabled: Boolean(queryKey),
  });

  useEffect(() => {
    if (!clinicaId || !leadId || !queryKey) return;

    const recoveryAbortController = new AbortController();
    let hasReconciledSubscription = false;

    const recoverGap = async () => {
      if (recoveryAbortController.signal.aborted) return;
      const current = queryClient.getQueryData<InfiniteData<ChatMessagePage>>(queryKey);
      const visibleMessages = flattenMessagesForDisplay(current);
      const latest = visibleMessages[visibleMessages.length - 1];
      if (!latest) {
        // A primeira página ainda é controlada pelo useInfiniteQuery; não iniciar uma segunda RPC concorrente.
        return;
      }

      let cursor: ChatMessageCursor = { createdAt: latest.created_at, id: latest.id };
      for (;;) {
        const recovered = await fetchMessagesAfter(clinicaId, leadId, cursor, recoveryAbortController.signal);
        if (recovered.length === 0) return;

        queryClient.setQueryData<InfiniteData<ChatMessagePage>>(queryKey, currentData => currentData
          ? mergeRecoveredMessages(currentData, recovered)
          : currentData,
        );

        const newest = recovered[recovered.length - 1];
        if (!newest || recovered.length < 100) return;
        cursor = { createdAt: newest.created_at, id: newest.id };
      }
    };

    const channel = supabase
      .channel(`chat-messages-${clinicaId}-${leadId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'chat_mensagens',
        filter: `lead_id=eq.${leadId}`,
      }, payload => {
        const row = (payload.new ?? payload.old) as MessageRpcRow;
        if (String(row.clinica_id) !== clinicaId) return;

        if (payload.eventType === 'DELETE') {
          queryClient.setQueryData<InfiniteData<ChatMessagePage>>(queryKey, current => current
            ? {
                ...current,
                pages: current.pages.map(page => ({
                  ...page,
                  messages: page.messages.filter(message => message.id !== String(row.id)),
                })),
              }
            : current,
          );
          return;
        }

        const message = normalizarMensagem(row);
        queryClient.setQueryData<InfiniteData<ChatMessagePage>>(queryKey, current => current
          ? appendRealtimeMessage(current, message)
          : current,
        );
      })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          hasReconciledSubscription = false;
        }
        if (status === 'SUBSCRIBED' && !hasReconciledSubscription) {
          // Cada reassinatura pode ter perdido eventos; o cursor recupera somente o delta, sem recarregar o histórico.
          hasReconciledSubscription = true;
          void recoverGap().catch(() => undefined);
        }
      });

    return () => {
      recoveryAbortController.abort();
      void supabase.removeChannel(channel);
    };
  }, [clinicaId, leadId, queryClient, queryKey]);

  return {
    ...query,
    messages: flattenMessagesForDisplay(query.data),
    fetchOlderMessages: query.fetchNextPage,
    hasOlderMessages: query.hasNextPage,
    isFetchingOlderMessages: query.isFetchingNextPage,
  };
};
